import React from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faChartLine,
  faChevronRight,
  faCloud,
  faMapLocationDot,
  faTemperatureHalf,
} from "@fortawesome/free-solid-svg-icons";
import "./ModuleSelection.css";

const MODULES = [
  {
    key: "electric-network",
    title: "Electric Network",
    eyebrow: "Active module",
    description: "Inspections, defect review, image evidence, maps, and reports.",
    icon: faBolt,
    accent: "#2563eb",
    route: "/modules/electric-network",
    stats: [
      { label: "Results", value: "AI" },
      { label: "Assets", value: "Lines" },
    ],
  },
  {
    key: "emissions",
    title: "Emissions",
    disabled: true,
    eyebrow: "Coming soon",
    description: "Methane campaigns, Sniffer4D imports, annotations, and evidence reports.",
    icon: faCloud,
    accent: "#059669",
    route: "/modules/emissions",
    stats: [
      { label: "Data", value: "CSV" },
      { label: "Maps", value: "CH4" },
    ],
  },
  {
    key: "thermal-pipeline",
    title: "Thermal",
    eyebrow: "New module",
    description: "Solar panels, thermal inspections, screening progress, and field reports.",
    icon: faTemperatureHalf,
    accent: "#dc2626",
    route: "/modules/thermal-pipeline",
    stats: [
      { label: "Images", value: "IR" },
      { label: "Review", value: "IR + RGB" },
    ],
  },
];

function ModuleCard({ module }) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      className="module-card"
      disabled={module.disabled}
      onClick={() => { if (!module.disabled) navigate(module.route); }}
      style={{ "--module-accent": module.accent }}
    >
      <span className="module-card-mark">
        <FontAwesomeIcon icon={module.icon} />
      </span>
      <span className="module-card-body">
        <span className="module-card-eyebrow">{module.eyebrow}</span>
        <span className="module-card-title">{module.title}</span>
        <span className="module-card-description">{module.description}</span>
        <span className="module-card-stats">
          {module.stats.map((stat) => (
            <span key={stat.label} className="module-stat">
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </span>
          ))}
        </span>
      </span>
      <span className="module-card-action">
        <FontAwesomeIcon icon={faChevronRight} />
      </span>
    </button>
  );
}

export function ModuleSelection() {
  return (
    <div className="module-page">
      <header className="module-header">
        <div>
          <p className="module-eyebrow">Aeryum modules</p>
          <h1>Choose a workspace</h1>
          <p className="module-subtitle">
            Start from the inspection domain you want to review.
          </p>
        </div>
      </header>

      <section className="module-grid" aria-label="Available modules">
        {MODULES.map((module) => (
          <ModuleCard key={module.key} module={module} />
        ))}
      </section>

      <section className="module-overview" aria-label="Platform overview">
        <div className="module-overview-item">
          <FontAwesomeIcon icon={faMapLocationDot} />
          <span>
            <strong>Projects</strong>
            <small>Shared client and asset context</small>
          </span>
        </div>
        <div className="module-overview-item">
          <FontAwesomeIcon icon={faChartLine} />
          <span>
            <strong>Reports</strong>
            <small>Evidence packages by inspection type</small>
          </span>
        </div>
      </section>
    </div>
  );
}

export function ModulePlaceholder({ title, subtitle, icon, accent }) {
  return (
    <div className="module-page">
      <section className="module-placeholder" style={{ "--module-accent": accent }}>
        <span className="module-placeholder-icon">
          <FontAwesomeIcon icon={icon} />
        </span>
        <p className="module-eyebrow">Module setup</p>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </section>
    </div>
  );
}

export const modulePlaceholderIcons = {
  emissions: faCloud,
  thermalPipeline: faTemperatureHalf,
};
